import {
  Button,
  CreateButton,
  ExportButton,
  Icons,
  List,
  notification,
  Popconfirm,
  ShowButton,
  Space,
  Table,
  TableProps,
  Tooltip,
  Typography,
} from '@pankod/refine-antd';
import { IResourceComponentsProps, useNavigation } from '@pankod/refine-core';
import { SearchWithDateRange } from 'components/search/SearchWithDateRange';
import CustomTableWithPagination from 'components/table/custom-table';
import { IDamageGood, IQueryOptionals, PaginationHandler } from 'interfaces';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { deleteDamage, getDamages, getDamagesDropdown } from 'services/damage';
import { SearchIndex } from 'services/meilisearch';
import { getActivityHistoryUrl } from 'utilities/activity-log';
import { downloadCSVFile } from 'utilities/file';
import { formatCurrency, getDateRangeOfLastXMonths } from 'utilities/shared';
import styles from './style.module.css';
import MetaData from 'utilities/metadata';

const { Text } = Typography;

export const DamageList: React.FC<IResourceComponentsProps> = () => {
  const [damageGoods, setDamageGoods] = useState<IDamageGood[]>([]);
  const [totalDamage, setTotalDamage] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [dateRange, setDateRange] = useState<{ startDate?: string; endDate?: string }>(() => {
    const [startDate, endDate] = getDateRangeOfLastXMonths(3);
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
  });
  const { push } = useNavigation();

  const query = useMemo<IQueryOptionals>(() => {
    return {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      searchString: searchText,
      start: page,
      limit,
    };
  }, [dateRange.startDate, dateRange.endDate, searchText, page, limit]);

  const fetchDamages = useCallback(async (query: IQueryOptionals) => {
    setIsLoading(true);
    try {
      const response = await getDamages(query);
      setDamageGoods(response.data);
      setTotalDamage(response.total);
    } catch (err) {
      if (err instanceof Error) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchDamages(query);
  }, [fetchDamages, query]);

  const handleDelete = async (id: string) => {
    try {
      await deleteDamage(id);
      await fetchDamages({
        startDate: query.startDate,
        endDate: query.endDate,
      });
    } catch (err) {
      return notification.error({
        message: 'Failed',
        description: 'Failed to delete.',
      });
    }
  };

  const onSearch = async (values: {
    startDate?: string;
    endDate?: string;
    searchString: string;
  }) => {
    setDateRange({ startDate: values.startDate, endDate: values.endDate });
    setSearchText(values.searchString);
    setPage(1);
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const productData = await getDamagesDropdown(query.startDate, query.endDate);

      const rows = [['Damaged Good ID', 'Date', 'Warehouse', 'Total Loss Amount']];
      productData.forEach((element) => {
        rows.push([
          element.id,
          element.damageDate.toString(),
          element.warehouse.name,
          element.totalAmount.toString(),
        ]);
      });
      downloadCSVFile(rows);
    } catch (err) {
      notification.error({
        message: 'Failed',
        description: 'Failed to Export CSV File.',
      });
    }
    setIsExporting(false);
  };

  const pageHeaders = (
    <Space>
      <CreateButton type="primary" />
      <ExportButton loading={isExporting} onClick={handleExport} />
      <Button
        icon={<Icons.HistoryOutlined />}
        onClick={() => push(getActivityHistoryUrl('damage'))}
      >
        Activity
      </Button>
    </Space>
  );

  const onTableChange: TableProps<IDamageGood>['onChange'] = async (
    pagination,
    _filters,
    _sorter,
    _extra
  ) => {
    const page = pagination.current ?? 1;
    const limit = pagination.pageSize ?? 10;
    setPage(page);
    setLimit(limit);
  };

  const onPageChange: PaginationHandler = async (page: number, pageSize: number) => {
    setPage(page);
    setLimit(pageSize);
  };

  return (
    <List headerProps={{ extra: pageHeaders }}>
      <MetaData />
      <SearchWithDateRange
        placeholder="Search by User, Damage ID, Product"
        size="large"
        className={styles['filter-section__search']}
        index={SearchIndex.Damage}
        groups={['user', 'id', 'product']}
        onSearch={onSearch}
      />
      <CustomTableWithPagination
        total={totalDamage}
        page={page}
        limit={limit}
        onPageChange={onPageChange}
      >
        <Table
          dataSource={damageGoods}
          loading={isLoading}
          rowKey="id"
          scroll={{ x: true }}
          onChange={onTableChange}
          // pagination={{
          //   defaultPageSize: 10,
          //   showSizeChanger: true,
          //   total: totalDamage,
          //   current: page,
          //   pageSize: limit,
          // }}
          pagination={false}
        >
          <Table.Column<IDamageGood> dataIndex="id" title="Damage Good ID" />
          <Table.Column<IDamageGood>
            dataIndex="damageDate"
            title="Date"
            render={(_, record): React.ReactNode => {
              const date = new Date(record.damageDate).toLocaleDateString('en-GB');
              return <Text>{date}</Text>;
            }}
          />
          <Table.Column<IDamageGood>
            dataIndex="totalAmount"
            title="Total Loss Amount"
            render={(_, record) => formatCurrency(record.totalAmount)}
          />
          <Table.Column<IDamageGood>
            dataIndex={['warehouse', 'name']}
            title="Warehouse"
            render={(value, _record) => {
              return <Text>{value}</Text>;
            }}
          />
          <Table.Column<IDamageGood>
            title="Actions"
            dataIndex="actions"
            width={120}
            render={(_, record): React.ReactNode => {
              const text = <span>User: {record.user ? record.user.name : 'N/A'}</span>;
              return (
                <Space>
                  <ShowButton size="small" hideText recordItemId={record.id} />
                  <Popconfirm
                    title="Are you sure?"
                    okText="Delete"
                    onConfirm={() => handleDelete(record.id)}
                  >
                    <Button size="small" danger icon={<Icons.DeleteOutlined />} />
                  </Popconfirm>
                  <Tooltip title={text}>
                    <Button size="small" icon={<Icons.InfoOutlined />} />
                  </Tooltip>
                </Space>
              );
            }}
          />
        </Table>
      </CustomTableWithPagination>
    </List>
  );
};
